<template>
    <div class="game__task" id="gameTaskContainer" ref="taskContainer"
         :class="{'visible-mobile': isMobileTaskVisible, 'is-table': tableOn}"
         :style="{'--current-player-color': currentPlayerColor}"
    >
        <portal to="modalPortal">
            <PlayAgainModal
                v-if="playAgainModalIsVisible && isMini"
                @cover="coverPlayAgainModal"
                @close="resetGame">
            </PlayAgainModal>
        </portal>
        <div class="game__task-inner">
            <span class="master-name"
                  v-if="master && !isMafia"
                  :title="master.name"
                  :style="{color: getMasterNameTextColor}"
            >
                {{master.name}}
            </span>
            <div class="game__task-info"
                 v-if="roundNameIsVisible || timerIsVisible || playAgainModalIsCovered"
                 :class="{'disabled': !mouseEventsAreAllowed}">
                <div class="game__status" v-if="roundNameIsVisible">
                    <toggle-input
                        :editable="roundNameIsEditable"
                        :placeholder="$t('message.new_game')"
                        :value="gameState.globalStatus || roundName"
                        @change.native="changeGlobalStatus($event.target.value)"></toggle-input>
                </div>
                <div class="game__timer" v-show="timerIsVisible" :class="{'overlaid': isMafia}">
                    <game-timer-inline ref="timer"
                                       :editable="timerIsEditable"
                                       :changeTimerFunction="changeTimer">
                    </game-timer-inline>
                </div>
                <button type="button"
                        class="play-again-modal__cover-btn"
                        v-if="playAgainModalIsCovered && isMini"
                        @click.stop="uncoverPlayAgainModal">
                    <span class="btn-bg"></span>
                    <svg>
                        <use xlink:href="#ic_uncover"></use>
                    </svg>
                </button>
            </div>
            <game-task-form
                v-if="taskIsManageable && taskFormVisible"
                taskName="main"
                :taskType="taskFormType"
                :players="players"
                :player-id="playerId"
                :sendTaskMethod="sendTask"
                @hideTaskForm="hideTaskForm"
                @imageLoad="checkTaskImageRatio"
                ref="taskForm"
            ></game-task-form>
            <template v-if="!tableOn">
                <div
                    class="game__task-view"
                    :class="{
                    'has-two-images': hasTwoImages(),
                    'is-both-vertical': hasTwoVerticalImages,
                    'is-first-vertical': isMainImageVertical,
                    'is-form-visible': taskFormVisible
                }"
                    v-if="isTasksVisible()"
                    @drop="fileDrop"
                    @dragenter.prevent=""
                    @dragover.prevent=""
                >
                    <game-task
                        taskName="main"
                        v-if="gameState.tasks.main"
                        :task="gameState.tasks.main"
                        :task-settings="taskSettings.main"
                        :player-id="playerId"
                        :players="players"
                        :disableDelete="disableDeleteTasks()"
                        :taskVisibility="TASK_VISIBILITY"
                        @taskImageLoaded="checkTaskImageRatio"
                        @deleteTask="deleteTask('main')"
                    ></game-task>

                    <game-task
                        taskName="additional"
                        v-if="gameState.tasks.additional && showAdditionalTask"
                        :task="gameState.tasks.additional"
                        :task-settings="taskSettings.additional"
                        :player-id="playerId"
                        :players="players"
                        :disableDelete="disableDeleteTasks()"
                        :taskVisibility="TASK_VISIBILITY"
                        @taskImageLoaded="checkTaskImageRatio"
                        @deleteTask="deleteTask('additional')"
                    ></game-task>

                    <div class="game__task-buttons">
                        <span class="game__task-next-player" v-if="isActive() && getNextTaskPlayerName()">
                            {{$t('message.next_player')}} — <span :style="{'color': getNextTaskButtonColor()}">{{getNextTaskPlayerName()}}</span>
                        </span>
                        <button
                            class="game__task-next"
                            type="button"
                            :style="{'backgroundColor': getNextTaskButtonStyle, 'color': getNextButtonColor(getNextTaskButtonStyle)}"
                            v-if="isActive() && isNextTaskButtonVisible()"
                            @click="showPresetTask"
                        >
                            {{$t('button.next')}}
                        </button>
                        <button
                            class="game__task-menu"
                            type="button"
                            v-if="isActive() && isEndGameButtonVisible()"
                            @click="endGame"
                        >
                            {{$t('button.back_to_menu')}}
                        </button>
                        <button
                            class="game__task-menu"
                            type="button"
                            v-if="isActive() && isPlayAgainButtonVisible()"
                            @click="playAgain"
                        >
                            {{$t('button.play_again')}}!
                        </button>
                    </div>

                </div>
            </template>
            <game-table v-else
                        ref="table"
                        :layout-type="layoutType"
                        :current-player-color="currentPlayerColor"
                        :form-is-visible="tableFormIsVisible"
                        :table-hand-instance="tableHandInstance"
                        :upload-cards-url="uploadCardsUrl"
                        :upload-background-url="uploadBackgroundUrl"
                        @hideTaskForm="hideAddCardsForm"
                        @toggleTable="toggleTable"
            ></game-table>

            <game-task-setup
                :taskVisibility="TASK_VISIBILITY"
                :players="players"
                :player-id="playerId"
                :table-mode-on="tableOn"
                :task-settings="taskSettings.main"
                :upload-background-url="uploadBackgroundUrl"
                @changeVisibility="changeTaskVisibility($event)"
                @changePlayerVisibility="changeTaskPlayerVisibility"
                @showTaskForm="showTaskForm"
                @deleteTask="deleteTask"
                @addCards="showAddCardsForm"
            ></game-task-setup>
        </div>
        <game-paused
            v-if="gameIsPaused"
            :gameIsPaused="gameIsPaused"
            @hide-paused-overlay="hidePausedOverlay"
        >
        </game-paused>
    </div>
</template>

<script>
import store from "../store";
import Tools from "../helpers/Tools";
import {Tool} from '../helpers/Tool';
import ErrorHandler from "../helpers/ErrorHandler";
import EventApi from "../api/EventsApi";
import MessageHelper from "../helpers/MessageHelper";
import TasksStateTypesEnums from "../../enums/tasks-state-types/TasksStateTypesEnums";
import MessageInformer from "./Informers/MessageInformer";
import PlayAgainModal from "./Modal/PlayAgainModal";

const TYPE_CHANGE_GLOBAL_STATUS = 'changeGlobalStatus';
const TYPE_CHANGE_TIMER = 'changeTimer';
const TYPE_SET_TASK = 'setTask';
const TYPE_DELETE_TASK = 'deleteTask';
const TASK_VISIBILITY = {
    HIDDEN: 0,
    VISIBLE: 1,
    VISIBLE_FOR: 2,
};

export default {
    components: {PlayAgainModal, MessageInformer},
    props: {
        isMobileTaskVisible: {
            required: true,
            type: Boolean
        },
        processTaskUrl: {
            required: true,
            type: String
        },
        gameState: {
            required: true,
            type: Object
        },
        uploadCardsUrl: {
            required: true,
            type: String
        },
        uploadBackgroundUrl: {
            required: true,
            type: String
        },
        tableOn: {
            required: true,
            type: Boolean
        },
        tableHandInstance: {
            required: true,
            type: Object
        },
        layoutType: {
            required: true,
            type: String
        }
    },

    data() {
        return {
            taskSettings: {
                main: {
                    visibility: null,
                    visibleFor: []
                },
                additional: {
                    visibility: null,
                    visibleFor: []
                }
            },
            TASK_VISIBILITY: TASK_VISIBILITY,
            taskFormType: '',
            taskFormVisible: false,
            isMainImageVertical: false,
            isAdditionalImageVertical: false,
            currentColor: '',
            colorTimer: null,
            buttonStyle: null,
            start: false,
            orangeColor: '#fe5616',
            tableFormIsVisible: false,
            oldTableIsVisible: true,
            mouseEventsAreAllowed: true,
            gameIsPaused: false,
            taskColumnWidth: '',
            playAgainModalIsVisible: false,
            playAgainModalIsCovered: false
        }
    },

    mounted() {
        if (this.gameState.automation) {
            this.currentColor = this.getNextTaskButtonColor();
        }

        this.$nextTick(() => {
            const observer = new ResizeObserver(entries => {
                for (let entry of entries) {
                    this.setTaskContainerWidth();
                }
            });

            if (typeof this.$refs.taskContainer !== 'undefined') {
                observer.observe(this.$refs.taskContainer);
            }
        });

        this.$emit('refresh-task-container');
        EventApi.addEventListener(EventApi.TABLE_DRAG_STARTED, this.disableLayoutInteractions);
        EventApi.addEventListener(EventApi.TABLE_DRAG_ENDED, this.enableLayoutInteractions);
        EventApi.addEventListener(EventApi.RESET_GAME, this.resetGame);
    },

    beforeDestroy() {
        EventApi.removeEventListener(EventApi.TABLE_DRAG_STARTED, this.disableLayoutInteractions);
        EventApi.removeEventListener(EventApi.TABLE_DRAG_ENDED, this.enableLayoutInteractions);
        EventApi.removeEventListener(EventApi.RESET_GAME, this.resetGame);
    },

    updated() {
        if (this.gameState.automation) {
            this.start = this.gameState.automation.tasksContainer.nextTaskButton.attention;

            if (!this.start) {
                this.currentColor = this.getNextTaskButtonColor();
            }
        }
    },

    computed: {
        players: () => store.getters.getPlayers,
        playerId: () => store.getters.getPlayerId,
        isMini: () => store.getters.getIsMini,
        hasTwoVerticalImages: function() {
            return this.hasTwoImages()
                && this.isMainImageVertical
                && this.isAdditionalImageVertical;
        },

        roundName: function() {
            const round = +this.gameState.tasks.state.currentRound ? this.gameState.tasks.state.currentRound : '1'
            return this.$t('message.round') + ' ' +  round;
        },

        /**
         * Настройки автоматизации игры
         */
        gameAutomation: () => store.getters.getGameState.automation,

        /**
         * Запущена мафия
         * @private
         */
        isMafia: function() {
            return !!this.gameState.zones.chat.buttons.mafia;
        },

        /**
         * Цвет игрока, который ходит
         */
        currentPlayerColor: function() {
            let color = '#F65A00';
            if (!this.gameAutomation) {
                return color;
            }

            let masterPlayerId = '';
            for (let player in this.gameAutomation.playersMap) {
                if (this.gameAutomation.playersMap[player].master) {
                    masterPlayerId = this.gameAutomation.playersMap[player].playerId;
                }
            }

            const filtered = this.players.filter(player => player.id === masterPlayerId);

            if (typeof filtered[0] !== 'undefined') {
                color = filtered[0].color.code;
            }

            return color;
        },

        /**
         * Цвет текста имени игрока, который ходит
         * @return {string}
         */
        getMasterNameTextColor: function () {
            // Если цвет игрока тёмный, возвращаем белый цвет, иначе чёрный
            return Tool.isDark(this.currentPlayerColor) ? '#FFFFFF' : '#000000';
        },

        /**
         * Стили кнопки "следующий игрок"
         */
        getNextTaskButtonStyle: {
            get: function() {
                if (!this.gameState.automation) {
                    return null;
                }
                return this.currentColor;
            },

            set: function(color) {
                this.currentColor = color;
            }
        },
        currentActivity: () => store.getters.getCurrentActivity,
        currentActivitySettings: function() {
            if (!this.currentActivity) {
                return null;
            }
            return this.currentActivity.settings;
        },
        showAdditionalTask: function() {
            if (!this.currentActivity) {
                return true;
            }
            return this.currentActivitySettings.cardsCount > 1;
        },
        roundNameIsVisible: function() {
            let roundNameVisibility = this.gameState.tasks.state.showRoundName;

            if (!this.currentActivitySettings) {
                return roundNameVisibility;
            }
            return this.currentActivitySettings.canViewRoundName && roundNameVisibility;
        },
        roundNameIsEditable: function () {
            if (!this.currentActivitySettings) {
                return true;
            }
            return this.currentActivitySettings.canManageRoundName;
        },
        taskIsManageable: function () {
            if (!this.currentActivitySettings) {
                return true;
            }
            return this.currentActivitySettings.canManageTasks;
        },
        timerIsVisible: function () {
            if (!this.gameState.timer) {
                return false;
            }
            let timerVisibility = this.gameState.timer.visible;

            if (!this.currentActivitySettings) {
                return timerVisibility;
            }
            return this.currentActivitySettings.canViewTimer && timerVisibility;
        },
        timerIsEditable: function () {
            if (!this.currentActivitySettings) {
                return true;
            }
            return this.currentActivitySettings.canManageTimer;
        },

        /**
         * Карта игроков автоматизации
         */
        playersMap: function () {
            if (!this.gameAutomation) {
                return null;
            }
            return this.gameAutomation.playersMap;
        },

        /**
         * ID ведущего игрока
         */
        masterId: function () {
            if (!this.playersMap) {
                return null;
            }

            let id = null;

            for (let player in this.playersMap) {
                if (this.playersMap[player].master) {
                    id = this.playersMap[player].playerId;
                }
            }

            return id;
        },

        master: function () {
            if (!this.masterId) {
                return null;
            }

            return store.getters.getPlayerGameDataById(this.masterId);
        },

        /**
         * Является гостем
         */
        isGuest: function() {
            return store.getters.getPlayerConnectionStatus === 'guest';
        }
    },

    watch: {
        start: function(newValue, oldValue)
        {
            if (newValue)
            {
                this.animateButtonColor();
            }
        },
        tableOn: function(newValue, oldValue)
        {
            if (newValue) {
                this.taskFormVisible = false;
            }
        },
        gameAutomation: function (newValue)
        {
            if (newValue && newValue.gameOver) {
                setTimeout(() => {
                    this.uncoverPlayAgainModal();
                }, 3000);
            }
        }
    },

    methods: {
        setTaskContainerWidth()
        {
            this.$nextTick(() => {
                if (typeof this.$refs.taskContainer === 'undefined') {
                    return;
                }
                this.taskColumnWidth = this.$refs.taskContainer.offsetWidth + 'px';
                EventApi.triggerEvent(EventApi.UPDATE_TASK_CONTAINER_WIDTH, this.taskColumnWidth);
            });
        },

        getNextButtonColor(color)
        {
            if (!color) {
                return '#fff';
            }
            const isDark = Tool.isDark(color);
            return isDark ? '#fff' : '#000';
        },

        /**
         * Задизайблить эл-ты интерфейса, чтоб они не перехватывали события мыши стола
         */
        disableLayoutInteractions()
        {
            this.mouseEventsAreAllowed = false;
        },

        /**
         * Включить события на эл-тах интерфейса
         */
        enableLayoutInteractions()
        {
            this.mouseEventsAreAllowed = true;
        },

        toggleTable(val)
        {
          this.oldTableIsVisible = val;
        },
        animateButtonColor()
        {
            if (!this.gameState.automation) {
                return null;
            }
            clearInterval(this.colorTimer);
            this.colorTimer = setInterval(() => {
                setTimeout(() => {
                    this.getNextTaskButtonStyle = this.getNextTaskButtonColor();
                }, 700);
                setTimeout(() => {
                    this.getNextTaskButtonStyle = this.orangeColor;
                }, 1400);
                setTimeout(() => {
                    this.getNextTaskButtonStyle = this.getNextTaskButtonColor();
                }, 2100);
            }, 5000);
        },
        /**
         * Изменить глобальный статус игры
         * @param value
         */
        changeGlobalStatus(value)
        {
            Tools.runTimer('globalStatus', () => {
                let data = {
                    userId: this.playerId,
                    globalStatus: value,
                };
                this.$parent.sendMessage(TYPE_CHANGE_GLOBAL_STATUS, data);
            });
        },

        /**
         * Изменить таймер игры
         * @param value
         * @param status
         */
        changeTimer(value, status)
        {
            Tools.runTimer('gameTimer', () => {
                let data = {
                    seconds: value,
                    status: status
                };
                this.$parent.sendMessage(TYPE_CHANGE_TIMER, data);
            });
        },

        /**
         * Есть два изображдения в заадачах
         * @returns {null}
         */
        hasTwoImages() {
            const mainTaskHasFile = this.gameState.tasks.main.fileUrl;
            const additionalTaskHasFile = this.gameState.tasks.additional.fileUrl;

            return mainTaskHasFile && additionalTaskHasFile;
        },

        /**
         * Отправка задания
         */
        sendTask(form, visibleFor, onSuccess, onError)
        {
            const formData = new FormData(form);

            formData.append('_token', Tools.getCSRFToken());

            let visibility = this.taskSettings.main.visibility;
            let visibleForRequest = this.taskSettings.main.visibleFor;
            if (visibleFor) {
                visibility = TASK_VISIBILITY.VISIBLE_FOR;
                visibleForRequest = visibleFor;
            }
            formData.append('visibility', visibility);

            if (visibleForRequest) {
                visibleForRequest.forEach((playerId) => {
                    if (playerId) {
                        formData.append('visibleFor[]', playerId);
                    }
                });
            }

            this.processTask(form, formData, onSuccess, onError);
        },

        /**
         * Показать форму добавления задачи
         * @param type
         */
        showTaskForm(type) {
            this.taskFormVisible = true;
            this.taskFormType = type;
        },

        /**
         * Скрыть форму добавления задачи
         */
        hideTaskForm() {
            this.taskFormVisible = false;
            this.taskFormType = '';
        },

        /**
         * Изменить видимость задания
         * @param type
         */
        changeTaskVisibility(type)
        {
            if (this.isGuest) {
                return;
            }
            this.taskSettings[type].visibleFor = [];
            if (this.taskSettings[type].visibility !== TASK_VISIBILITY.VISIBLE) {
                this.taskSettings[type].visibility = TASK_VISIBILITY.VISIBLE;
                this.players.forEach((player) => {
                    this.taskSettings[type].visibleFor.push(player.id);
                });
            } else {
                this.taskSettings[type].visibility = TASK_VISIBILITY.HIDDEN;
            }

            this.updateTask(type);
        },

        /**
         * Изменить видимость задания для конкретного игрока
         * @param playerId
         * @param type
         */
        changeTaskPlayerVisibility(playerId, type)
        {
            if (this.isGuest) {
                return;
            }
            const task = this.taskSettings[type];

            let wasVisibleForAll = false;
            if (task.visibility === TASK_VISIBILITY.VISIBLE) {
                wasVisibleForAll = true;
            }

            task.visibility = TASK_VISIBILITY.VISIBLE_FOR;

            if (task.visibleFor.indexOf(playerId) !== -1) {
                task.visibleFor.splice(task.visibleFor.indexOf(playerId), 1);
            } else {
                if (wasVisibleForAll) {
                    this.players.forEach((player) => {
                        if (player.id !== playerId) {
                            task.visibleFor.push(player.id);
                        }
                    });
                } else {
                    task.visibleFor.push(playerId);
                }
            }

            if (task.visibleFor.length > this.players.length) {
                task.visibility = TASK_VISIBILITY.VISIBLE;
                task.visibleFor = [];
            }

            if (task.visibleFor.length === 0 && task.visibility !== TASK_VISIBILITY.VISIBLE) {
                task.visibility = TASK_VISIBILITY.HIDDEN;
            }

            this.updateTask(type);
        },

        showAddCardsForm()
        {
            this.tableFormIsVisible = true;
        },

        hideAddCardsForm()
        {
            this.tableFormIsVisible = false;
        },

        /**
         * Изменить задание
         */
        updateTask(taskName)
        {
            let taskData = Tools.clone(this.gameState.tasks[taskName]);
            const settings = this.taskSettings[taskName];

            taskData.visibleFor = settings.visibleFor;
            taskData.visibility = settings.visibility;
            taskData.type = taskName;

            this.$parent.sendMessage(TYPE_SET_TASK, taskData);
        },

        /**
         * Отправляет данные задания для подготовки на сервере
         */
        processTask(form, formData, onSuccess, onError)
        {
            $.ajax({
                url: this.processTaskUrl,
                data: formData,
                processData: false,
                contentType: false,
                type: 'POST',
                success: (data) => {
                    if (!data.success) {
                        if (onError) {
                            onError(data);
                        } else {
                            ErrorHandler.error('Ошибка сохранения задания');
                        }
                    } else {
                        this.$parent.sendMessage(TYPE_SET_TASK, data.data);
                        form.elements.file.value = '';
                        this.hideTaskForm();

                        if (onSuccess) {
                            onSuccess(data);
                        }
                    }
                },
                error: (data) => {
                    if (onError) {
                        onError(data);
                    } else {
                        ErrorHandler.error('Ошибка запроса');
                    }
                }
            });
        },

        /**
         * Обновляет настройки задания согласно состоянию с сервер
         */
        syncTaskSettings(taskName)
        {
            let visibility = this.gameState.tasks[taskName].visibility;
            let visibleFor = this.gameState.tasks[taskName].visibleFor;

            this.taskSettings[taskName].visibility = visibility;
            this.taskSettings[taskName].visibleFor = visibleFor;
        },


        /**
         * Удалить задачу
         * @param taskType
         */
        deleteTask(taskType) {
            let data = {
                type: taskType
            };
            this.$parent.sendMessage(TYPE_DELETE_TASK, data);
        },

        /**
         * Найти игрока по id
         * @param playerId
         * @return {*}
         */
        findPlayer(playerId)
        {
            let result = null;
            this.players.forEach((player) => {
                if (player.id === playerId) {
                    result = player;
                }
            });
            return result;
        },

        showTimer(timer, currentTime) {
            if (this.$refs.timer) {
                this.$refs.timer.showTimer(timer, currentTime);
            }
        },

        /**
         * Показать задания
         * @return {boolean}
         */
        isTasksVisible()
        {
           return  this.gameState.tasks.state.type === TasksStateTypesEnums.STATE_CUSTOM_TYPE ||
               this.gameState.tasks.state.type === TasksStateTypesEnums.STATE_PRESET_TYPE;
        },

        /**
         * Показать подготовленные задания
         */
        showPresetTask()
        {
            const data = {
                contentPackId: this.gameState.tasks.state.content_pack_id,
                setId: this.gameState.tasks.state.set_id
            };
            MessageHelper.getContentPackTasksMessage(data);
        },

        /**
         * Показать кнопку следущее задание
         * @return {boolean|boolean}
         */
        isNextTaskButtonVisible()
        {
            let taskState = this.gameState.tasks.state;
            return taskState.type === TasksStateTypesEnums.STATE_PRESET_TYPE &&
                !taskState.show_end_button && !taskState.show_play_again_button;
        },

        /**
         * Можно удалять задачи
         */
        disableDeleteTasks()
        {
            return this.gameState.tasks.state.type === TasksStateTypesEnums.STATE_PRESET_TYPE;
        },

        /**
         * Завершить игру
         */
        endGame()
        {
            MessageHelper.sendMessage('resetState', {});
        },

        /**
         * Кнопка завершения игры видна
         */
        isEndGameButtonVisible()
        {
            return this.gameState.tasks.state.type === TasksStateTypesEnums.STATE_PRESET_TYPE &&
                this.gameState.tasks.state.show_end_button;
        },

        /**
         * Сыргать еще раз
         */
        playAgain()
        {
            const data = {
                contentPackId: this.gameState.tasks.state.content_pack_id,
                setId: 1,
                activityId: this.gameState.tasks.state.activity_id,
                playAgain: true,
            };
            this.$parent.sendMessage('getContentPackTasks', data);
        },

        /**
         * Кнопка завершения игры видна
         */
        isPlayAgainButtonVisible()
        {
            return this.gameState.tasks.state.type === TasksStateTypesEnums.STATE_PRESET_TYPE &&
                this.gameState.tasks.state.show_play_again_button;
        },

        /**
         * Драг-н-дроп картинки на область задач
         * @param event
         */
        fileDrop(event) {
            event.preventDefault();
            const taskFormFiles = event.dataTransfer.files;

            if (!(this.gameState.tasks.main.is_filled && this.gameState.tasks.additional.is_filled)) {
                this.showTaskForm('image');
            }
            this.$nextTick(() => {
                if (this.$refs.taskForm) {
                    this.$refs.taskForm.setFiles(taskFormFiles);
                }
            });
        },

        checkTaskImageRatio(data) {
            const img = data.img;
            const isVertical = img.naturalWidth <= img.naturalHeight;

            if (data.task === 'main') {
                this.isMainImageVertical = isVertical;
            } else if (data.task === 'additional') {
                this.isAdditionalImageVertical = isVertical;
            }
        },

        /**
         * Обработка нажатия кнопки задания
         * @param type
         */
        processTaskButtonClick(type)
        {
            this.$parent.sendMessage('taskButton', {
                type: type
            });
        },

        /**
         * Цвет кнопки "Дальше"
         */
        getNextTaskButtonColor()
        {
            if (!this.gameState.automation) {
                return null;
            }

            let playerId = this.gameState.automation.tasksContainer.nextTaskButton.playerId;

            if (!playerId) {
                return null;
            }

            return this.$parent.getPlayerColor(playerId);
        },

        /**
         * Цвет кнопки "Дальше"
         */
        getNextTaskPlayerName()
        {
            if (!this.gameState.automation) {
                return null;
            }

            if (typeof store.getters.getGameState.playersData === "undefined") {
                return null;
            }

            let playersData = store.getters.getGameState.playersData;

            let playerId = this.gameState.automation.tasksContainer.nextTaskButton.playerId;

            if (!playerId) {
                return null;
            }

            const player = playersData[playerId];

            if (!player.name) {
                return null;
            }

            return player.name;
        },


        /** Статус подключения игрока (guest, active, pending) */
        playerConnectionStatus() {
            return store.getters.getPlayerConnectionStatus;
        },


        /** Статус подключения: активный игрок на сите */
        isActive() {
            return this.playerConnectionStatus() === 'active';
        },


        /**
         *  Продолжить игру
         */
        hidePausedOverlay()
        {
            this.gameIsPaused = false;
        },

        /**
         * Развернуть модалку Играть еще
         */
        uncoverPlayAgainModal()
        {
            this.playAgainModalIsVisible = true;
            this.playAgainModalIsCovered = false;
        },

        /**
         * Свернуть модалку Играть еще
         */
        coverPlayAgainModal()
        {
            this.playAgainModalIsVisible = false;
            this.playAgainModalIsCovered = true;
        },

        resetGame()
        {
            this.playAgainModalIsVisible = false;
            this.playAgainModalIsCovered = false;
        }
    }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
    height: 190px;
    margin-top: 10px;
    opacity: 1;
    transition: height 0.3s ease,
    margin-top 0.3s ease,
    opacity 0.3s ease;
}
.fade-enter, .fade-leave-to {
    opacity: 0;
    height: 0;
    margin-top: 0;
    transition: height 0.3s ease,
    margin-top 0.3s ease,
    opacity 0.3s ease;
}
</style>
